/**
 * URL patterns for pages that are English-only and should not be
 * redirected to localized versions.
 *
 * These patterns are used by:
 * - Language redirect logic (src/utils/routes.ts)
 * - Sitemap exclusion (gatsby-config.ts)
 * - Locale page generation (gatsby-node.ts)
 */

// Regex patterns for checking URLs at runtime (language redirect)
export const ENGLISH_ONLY_PATTERNS: RegExp[] = [
  /^\/tutoring(\/|$)/, // /tutoring and /tutoring/*
  /^\/lp(\/|$)/, // /lp and /lp/*
  /^\/districts(\/|$)/, // /districts and /districts/*
  /^\/for(\/|$)/, // /for/<slug> -- English-only audience landing pages
];

// Glob patterns for sitemap exclusion (build time)
// Note: Only non-indexable pages should be here
export const SITEMAP_EXCLUSIONS: string[] = [
  `/lp/*`, // Landing pages - not indexable
  `/ul/*`, // Universal links - noindex pages (no localized versions generated)
  `/email/*`, // Email management pages - not indexable
  `/photo-updates`, // Photo uploads incident notice - not indexable
  `/dojo-islands/the-islands`, // Unlinked prototype - was Disallow-ed on the Next site
  `/*/dojo-islands/the-islands`, // ...and its locale copies
];

// Regex patterns for paths that get NO locale-prefixed page variants at build
// time. Pages matching these are built only at their primary URL, so
// /es-mx/<path> etc. are never created and return a 404.
// Note: this is stricter than ENGLISH_ONLY_PATTERNS - that list only stops
// runtime redirects and hreflang tags for pages whose locale copies still exist.
export const NO_LOCALE_PAGE_PATTERNS: RegExp[] = [
  /^\/ul\//, // Universal links - noindex, English only
  /^\/tutoring(\/|$)/, // Dojo Tutor - English only, copy is not translated
];

/**
 * Check if a pathname should be excluded from language redirection
 */
export const shouldSkipLanguageRedirect = (pathname: string): boolean => {
  return ENGLISH_ONLY_PATTERNS.some((pattern) => pattern.test(pathname));
};

/**
 * Check if a page path should skip locale-prefixed page generation at build time
 */
export const shouldSkipLocalePageGeneration = (path: string): boolean => {
  return NO_LOCALE_PAGE_PATTERNS.some((pattern) => pattern.test(path));
};
